import { Injectable, signal, computed } from '@angular/core';

import { Subject, Observable } from 'rxjs';

import {
  AlertConfig,
  AlertResult,
  AlertObserver,
  AlertQueueItem,
} from '@shared-models';

/**
 * Modern Alert Service following Angular 19 best practices
 * Provides a queue-based alert system with reactive state management
 */
@Injectable({ providedIn: 'root' })
export class AlertService {
  // Reactive state using signals
  private readonly queue = signal<AlertQueueItem[]>([]);
  private readonly isActive = signal<boolean>(false);

  // Computed signals for derived state
  readonly hasActiveAlert = computed(() => this.isActive());
  readonly queueLength = computed(() => this.queue().length);
  readonly isEmpty = computed(() => this.queue().length === 0);

  // Observable for alert display
  private readonly alertSubject = new Subject<AlertQueueItem | null>();
  readonly alert$: Observable<AlertQueueItem | null> =
    this.alertSubject.asObservable();

  constructor() {
    // Initialize with null to indicate no active alert
    this.alertSubject.next(null);
  }

  /**
   * Opens a new alert and adds it to the queue
   * @param config - Alert configuration
   * @returns Object with subscribe method for handling results
   */
  open(config: AlertConfig): { subscribe: (observer: AlertObserver) => void } {
    const subject = new Subject<AlertResult>();
    const queueItem: AlertQueueItem = { config, subject };

    // Update queue using signal
    this.queue.update((currentQueue) => [...currentQueue, queueItem]);

    this.processQueue();

    return {
      subscribe: (observer: AlertObserver) => {
        subject.subscribe({
          next: (result) => {
            this.handleAlertResult(result, observer);
          },
          error: (error) => {
            console.error('Alert subscription error:', error);
          },
        });
      },
    };
  }

  /**
   * Resolves the current alert with the specified action
   * @param action - Action performed by user
   * @param data - Optional data to pass with the result
   */
  resolve(action: 'accept' | 'reject' | 'close', data?: unknown): void {
    const currentQueue = this.queue();

    if (currentQueue.length === 0) {
      console.warn('Attempted to resolve alert but queue is empty');
      return;
    }

    const current = currentQueue[0];
    const result: AlertResult = { action, data };

    // Emit result and complete the subject
    current.subject.next(result);
    current.subject.complete();

    // Remove processed item from queue
    this.queue.update((queue) => queue.slice(1));
    this.isActive.set(false);

    // Process next item in queue
    this.processQueue();
  }

  /**
   * Clears all alerts from the queue
   */
  clearQueue(): void {
    const currentQueue = this.queue();

    // Complete all pending subjects
    currentQueue.forEach((item) => {
      item.subject.complete();
    });

    // Clear queue and reset state
    this.queue.set([]);
    this.isActive.set(false);
    this.alertSubject.next(null);
  }

  /**
   * Gets the current alert configuration (if any)
   */
  getCurrentAlert(): AlertConfig | null {
    const currentQueue = this.queue();
    return currentQueue.length > 0 ? currentQueue[0].config : null;
  }

  /**
   * Handles the result from an alert and calls appropriate observer methods
   * @param result - Alert result
   * @param observer - Observer with callback methods
   */
  private handleAlertResult(
    result: AlertResult,
    observer: AlertObserver,
  ): void {
    switch (result.action) {
      case 'accept':
        observer.accept?.(result.data);
        break;
      case 'reject':
        observer.reject?.(result.data);
        break;
      case 'close':
        observer.close?.();
        break;
      default:
        console.warn(`Unknown alert action: ${result.action}`);
    }
  }

  /**
   * Processes the alert queue by showing the next alert
   */
  private processQueue(): void {
    const currentQueue = this.queue();
    const currentlyActive = this.isActive();

    // Don't process if already active or queue is empty
    if (currentlyActive || currentQueue.length === 0) {
      // If not active and queue is empty, emit null to hide any alerts
      if (!currentlyActive && currentQueue.length === 0) {
        this.alertSubject.next(null);
      }
      return;
    }

    // Activate the first alert in queue
    this.isActive.set(true);
    this.alertSubject.next(currentQueue[0]);
  }
}
